import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, TrendingUp, TrendingDown, DollarSign, Wallet, Users, Loader2,
  Banknote, CreditCard, ArrowRightLeft, PieChart, Building2,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../lib/utils';
import { getFinanceSummary, getBranches } from '../lib/api';

const TABS = [
  { key: 'balance', label: 'Balance', icon: PieChart },
  { key: 'ingresos', label: 'Ingresos', icon: TrendingUp },
  { key: 'gastos', label: 'Gastos', icon: TrendingDown },
  { key: 'nomina', label: 'Nómina', icon: Users },
];

export default function AdminFinanceBalancePage() {
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('balance');
  const [branches, setBranches] = useState([]);
  const [activeBranchId, setActiveBranchId] = useState(null);

  useEffect(() => {
    getBranches().then((r) => setBranches(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    getFinanceSummary(month, activeBranchId)
      .then((r) => setData(r.data))
      .catch(() => toast.error('Error al cargar resumen'))
      .finally(() => setLoading(false));
  }, [month, activeBranchId]);

  const monthLabel = useMemo(() => {
    const [y, m] = month.split('-');
    return new Date(parseInt(y), parseInt(m) - 1).toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
  }, [month]);

  const activeBranchName = activeBranchId
    ? (branches.find((b) => b.id === activeBranchId)?.name || 'Sucursal')
    : 'General';

  const ingresos = data?.income?.total || 0;
  const gastos = data?.expenses?.total || 0;
  const nomina = data?.payroll?.total || 0;
  const totalEgresos = gastos + nomina;
  const utilidad = ingresos - totalEgresos;
  const margen = ingresos > 0 ? (utilidad / ingresos) * 100 : 0;

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fadeIn">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <Link to="/admin/finanzas" className="text-sm text-blue-600 inline-flex items-center gap-1 hover:underline">
              <ArrowLeft className="w-4 h-4" /> Volver a Finanzas
            </Link>
            <h1 className="text-3xl font-bold text-slate-900 mt-1">Balance Mensual</h1>
            <p className="text-slate-600 mt-1 capitalize">
              {monthLabel}
              {activeBranchId && <span className="ml-1 text-blue-600 font-medium">· {activeBranchName}</span>}
            </p>
          </div>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-auto" data-testid="balance-month" />
        </div>

        {/* Branch tabs (General + one per branch) */}
        <div className="flex flex-wrap gap-1 border-b border-slate-200" data-testid="balance-branch-tabs">
          <button
            onClick={() => setActiveBranchId(null)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeBranchId === null ? 'border-blue-600 text-blue-700 bg-blue-50/60' : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
            data-testid="balance-branch-tab-general"
          >
            <PieChart className="w-4 h-4" /> General
          </button>
          {branches.map((b) => (
            <button
              key={b.id}
              onClick={() => setActiveBranchId(b.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeBranchId === b.id ? 'border-blue-600 text-blue-700 bg-blue-50/60' : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
              data-testid={`balance-branch-tab-${b.id}`}
            >
              <Building2 className="w-4 h-4" /> {b.name}
            </button>
          ))}
        </div>

        <div className="flex gap-2 border-b border-slate-200" data-testid="balance-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                tab === t.key ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
              data-testid={`tab-${t.key}`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>

        {loading || !data ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
        ) : (
          <>
            {tab === 'balance' && <BalanceTab ingresos={ingresos} gastos={gastos} nomina={nomina} utilidad={utilidad} margen={margen} data={data} />}
            {tab === 'ingresos' && <IngresosTab data={data} />}
            {tab === 'gastos' && <GastosTab data={data} />}
            {tab === 'nomina' && <NominaTab data={data} />}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

function KpiBig({ label, value, sub, color = 'blue', icon: Icon }) {
  const palette = {
    emerald: 'from-emerald-500 to-emerald-600',
    rose: 'from-rose-500 to-rose-600',
    blue: 'from-blue-500 to-blue-600',
    violet: 'from-violet-500 to-violet-600',
  };
  return (
    <div className={`rounded-2xl bg-gradient-to-br ${palette[color]} text-white p-5 shadow-sm`} data-testid={`kpi-${label.toLowerCase()}`}>
      <div className="flex items-center gap-2 text-xs uppercase font-semibold opacity-90">
        <Icon className="w-4 h-4" />{label}
      </div>
      <div className="text-3xl font-bold mt-2 tabular-nums">{value}</div>
      {sub && <div className="text-xs opacity-80 mt-1">{sub}</div>}
    </div>
  );
}

function BalanceTab({ ingresos, gastos, nomina, utilidad, margen, data }) {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiBig label="Ingresos" value={formatCurrency(ingresos)} sub={`${data.income.orders_count} ventas`} color="emerald" icon={TrendingUp} />
        <KpiBig label="Gastos" value={formatCurrency(gastos)} sub="Renta, servicios, otros" color="rose" icon={TrendingDown} />
        <KpiBig
          label="Nómina"
          value={formatCurrency(nomina)}
          sub={data.payroll.is_partial
            ? `${data.payroll.employees?.length || 0} empleados · ${(data.payroll.days_elapsed / 7).toFixed(1)} sem (día ${data.payroll.day_of_month}/${data.payroll.days_in_month})`
            : `${data.payroll.employees?.length || 0} empleados · ${(data.payroll.days_in_month / 7).toFixed(1)} sem`}
          color="violet"
          icon={Users}
        />
        <KpiBig label="Utilidad" value={formatCurrency(utilidad)} sub={`Margen ${margen.toFixed(1)}%`} color={utilidad >= 0 ? 'blue' : 'rose'} icon={Wallet} />
      </div>

      {data.payroll.is_partial && (
        <div className="rounded-lg bg-violet-50 border border-violet-200 p-3 text-sm text-violet-800 flex items-start gap-2" data-testid="payroll-prorate-notice">
          <Users className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            Sueldo base <b>semanal</b>. La nómina del mes se prorratea al avance del periodo: <b>día {data.payroll.day_of_month} de {data.payroll.days_in_month}</b> ({data.payroll.days_elapsed} días pagados a {(data.payroll.days_elapsed / 7).toFixed(1)} semanas).
          </div>
        </div>
      )}

      {/* Estado de resultados estilo contable */}
      <div className="card-base p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-3">Estado de Resultados</h2>
        <table className="w-full text-sm">
          <tbody>
            <tr><td colSpan={2} className="pt-3 font-bold uppercase text-xs text-emerald-600">Ingresos</td></tr>
            <tr><td className="py-1 pl-4">Efectivo</td><td className="py-1 text-right font-mono">{formatCurrency(data.income.by_method?.efectivo || 0)}</td></tr>
            <tr><td className="py-1 pl-4">Tarjeta</td><td className="py-1 text-right font-mono">{formatCurrency(data.income.by_method?.tarjeta || 0)}</td></tr>
            <tr><td className="py-1 pl-4">Transferencia</td><td className="py-1 text-right font-mono">{formatCurrency(data.income.by_method?.transferencia || 0)}</td></tr>
            <tr className="border-t border-slate-200"><td className="py-2 font-semibold">TOTAL INGRESOS</td><td className="py-2 text-right font-bold font-mono text-emerald-600">{formatCurrency(ingresos)}</td></tr>

            <tr><td colSpan={2} className="pt-4 font-bold uppercase text-xs text-rose-600">Egresos</td></tr>
            <tr><td className="py-1 pl-4">Nómina ({data.payroll.employees?.length || 0} empleados)</td><td className="py-1 text-right font-mono">{formatCurrency(nomina)}</td></tr>
            {Object.entries(data.expenses.by_category || {}).map(([cat, amt]) => (
              <tr key={cat}><td className="py-1 pl-4 capitalize">{cat}</td><td className="py-1 text-right font-mono">{formatCurrency(amt)}</td></tr>
            ))}
            <tr className="border-t border-slate-200"><td className="py-2 font-semibold">TOTAL EGRESOS</td><td className="py-2 text-right font-bold font-mono text-rose-600">{formatCurrency(gastos + nomina)}</td></tr>

            <tr className="border-t-2 border-slate-400 bg-slate-50"><td className="py-3 font-bold text-base">UTILIDAD NETA</td><td className={`py-3 text-right font-bold font-mono text-lg ${utilidad >= 0 ? 'text-blue-700' : 'text-red-600'}`}>{formatCurrency(utilidad)}</td></tr>
            <tr><td className="py-1 text-xs text-slate-500">Margen</td><td className="py-1 text-right text-xs text-slate-500">{margen.toFixed(1)}%</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function IngresosTab({ data }) {
  const total = data.income.total;
  const bm = data.income.by_method;
  return (
    <div className="card-base p-6 space-y-4">
      <h2 className="text-lg font-semibold text-slate-900">Ingresos del mes</h2>
      <p className="text-3xl font-bold text-emerald-600 tabular-nums">{formatCurrency(total)}</p>
      <p className="text-sm text-slate-500">{data.income.orders_count} órdenes pagadas</p>
      <div className="space-y-3">
        <MethodRow icon={Banknote} label="Efectivo" amount={bm.efectivo || 0} total={total} color="emerald" />
        <MethodRow icon={CreditCard} label="Tarjeta" amount={bm.tarjeta || 0} total={total} color="blue" />
        <MethodRow icon={ArrowRightLeft} label="Transferencia" amount={bm.transferencia || 0} total={total} color="violet" />
      </div>
    </div>
  );
}

function GastosTab({ data }) {
  const total = data.expenses.total;
  return (
    <div className="card-base p-6 space-y-4">
      <h2 className="text-lg font-semibold text-slate-900">Gastos fijos del mes</h2>
      <p className="text-3xl font-bold text-rose-600 tabular-nums">{formatCurrency(total)}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h3 className="text-sm font-semibold text-slate-700 uppercase mb-2">Por categoría</h3>
          {Object.keys(data.expenses.by_category || {}).length === 0 ? (
            <p className="text-slate-400 text-sm">Sin gastos</p>
          ) : (
            <ul className="space-y-1">
              {Object.entries(data.expenses.by_category).map(([cat, amt]) => (
                <li key={cat} className="flex justify-between p-2 bg-slate-50 rounded text-sm">
                  <span className="capitalize">{cat}</span>
                  <span className="font-mono font-semibold">{formatCurrency(amt)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <h3 className="text-sm font-semibold text-slate-700 uppercase mb-2 flex items-center gap-1"><Building2 className="w-4 h-4" />Por sucursal</h3>
          {Object.keys(data.expenses.by_branch || {}).length === 0 ? (
            <p className="text-slate-400 text-sm">Sin gastos</p>
          ) : (
            <ul className="space-y-1">
              {Object.entries(data.expenses.by_branch).map(([name, info]) => (
                <li key={name} className="flex justify-between p-2 bg-slate-50 rounded text-sm">
                  <span>{name}</span>
                  <span className="font-mono font-semibold">{formatCurrency(info.total || info)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function NominaTab({ data }) {
  const employees = data.payroll.employees || [];
  const isPartial = data.payroll.is_partial;
  const weeksLabel = `${(data.payroll.days_elapsed / 7).toFixed(2)} semanas`;
  return (
    <div className="card-base p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Nómina del mes</h2>
          <p className="text-xs text-slate-500">Base semanal · {data.payroll.days_elapsed} días pagados ({weeksLabel}) de {data.payroll.days_in_month}</p>
        </div>
        <div className="text-right">
          <span className="text-2xl font-bold text-violet-600 tabular-nums">{formatCurrency(data.payroll.total)}</span>
          {isPartial && (
            <div className="text-xs text-violet-700">Prorrateado al día {data.payroll.day_of_month}</div>
          )}
        </div>
      </div>
      {employees.length === 0 ? (
        <p className="text-slate-400 text-center py-8">Sin empleados registrados</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="table-header">Empleado</th>
                <th className="table-header">Rol</th>
                <th className="table-header text-right">Sueldo semanal</th>
                <th className="table-header text-right">Sueldo {isPartial ? 'parcial' : 'mes'}</th>
                <th className="table-header text-right">Bonos</th>
                <th className="table-header text-right">Total</th>
                {isPartial && <th className="table-header text-right text-slate-400">Mes completo</th>}
              </tr>
            </thead>
            <tbody>
              {employees.map((e, i) => (
                <tr key={i} className="table-row">
                  <td className="table-cell font-medium">{e.name}</td>
                  <td className="table-cell capitalize text-slate-600">{e.role || '—'}</td>
                  <td className="table-cell text-right font-mono text-slate-500">{formatCurrency(e.weekly_salary || 0)}</td>
                  <td className="table-cell text-right font-mono">{formatCurrency(e.salary || 0)}</td>
                  <td className="table-cell text-right font-mono">{formatCurrency(e.bonus || 0)}</td>
                  <td className="table-cell text-right font-mono font-bold">{formatCurrency(e.total || 0)}</td>
                  {isPartial && <td className="table-cell text-right font-mono text-slate-400">{formatCurrency(e.total_full || 0)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MethodRow({ icon: Icon, label, amount, total, color }) {
  const pct = total > 0 ? Math.round((amount / total) * 100) : 0;
  const colors = { emerald: 'bg-emerald-400', blue: 'bg-blue-400', violet: 'bg-violet-400' };
  return (
    <div className="flex items-center gap-3">
      <Icon className="w-4 h-4 text-slate-500" />
      <div className="flex-1">
        <div className="flex justify-between mb-1">
          <span className="text-sm font-medium">{label}</span>
          <span className="text-sm font-semibold tabular-nums">{formatCurrency(amount)}</span>
        </div>
        <div className="w-full bg-slate-100 rounded-full h-1.5">
          <div className={`h-1.5 rounded-full ${colors[color]}`} style={{ width: `${pct}%` }} />
        </div>
      </div>
      <span className="text-xs text-slate-500 w-10 text-right">{pct}%</span>
    </div>
  );
}
